// Theme Widgets Script with Modern Scroll-Trigger Animations & Counter Effects
import {
  type ThemeMode,
  getStoredThemeMode,
  applyThemeMode,
  toggleThemeMode,
  cycleThemeMode,
  bindThemeToggleListeners,
  setupMobileMenu,
  initScrollAnimations,
  initStickyNavScroll,
  initLivingElevationViewport,
  initAuthFormSync,
  initSocialShare as widgetInitSocialShare,
  initAnnouncementBars,
  initNavFavicon,
  hideEmptyValidationSummaries,
  cleanupEmptyFlashMessages,
  initAuthTabSwitchers,
  initPasswordToggles,
  initCarousels,
  initContactForm,
  initLightboxes,
  initNewsletterForm,
  initPreviewStudioListener,
  getShopifyCartState,
  openCartDrawer,
  closeCartDrawer,
  toggleCartDrawer,
  addShopifyItem,
  updateShopifyItemQuantity,
  removeShopifyItem,
  clearShopifyCart,
  initShopifyStorefrontElementsInternal,
  selectProductVariantInternal,
  initProductVariantSelectorsInternal,
  initShopifyAppBlocksInternal,
  selectProductVariant,
  initProductVariantSelectors,
  initShopifyStorefrontElements,
  initShopifyAppBlocks,
  afOpenCartDrawer,
  afCloseCartDrawer,
  afToggleCartDrawer,
  afAddShopifyItem,
  afGetShopifyCart,
  afClearShopifyCart,
  afUpdateCartBadge,
  updateCartBadge,
  afSelectProductVariant,
  afInitProductVariantSelectors,
  afInitShopifyAppBlocks,
  registerDraftingStudioElement,
} from './widgets/index.ts';

export * from './widgets/index.ts';

export type { ThemeMode };

export {
  getShopifyCartState,
  openCartDrawer,
  closeCartDrawer,
  toggleCartDrawer,
  addShopifyItem,
  updateShopifyItemQuantity,
  removeShopifyItem,
  clearShopifyCart,
  initShopifyStorefrontElementsInternal,
  selectProductVariantInternal,
  initProductVariantSelectorsInternal,
  initShopifyAppBlocksInternal,
  selectProductVariant,
  initProductVariantSelectors,
  initShopifyStorefrontElements,
  initShopifyAppBlocks,
  afOpenCartDrawer,
  afCloseCartDrawer,
  afToggleCartDrawer,
  afAddShopifyItem,
  afGetShopifyCart,
  afClearShopifyCart,
  afUpdateCartBadge,
  updateCartBadge,
  afSelectProductVariant,
  afInitProductVariantSelectors,
  afInitShopifyAppBlocks,
};

export let initSocialShare: (container?: HTMLElement | Document) => void = function (
  container?: HTMLElement | Document
): void {
  widgetInitSocialShare(container);
};

export let afGetThemeMode: () => ThemeMode = function (): ThemeMode {
  if (
    typeof window !== 'undefined' &&
    typeof window.afGetThemeMode === 'function' &&
    window.afGetThemeMode !== afGetThemeMode
  ) {
    return window.afGetThemeMode();
  }
  return getStoredThemeMode();
};

export let afSetThemeMode: (mode: ThemeMode) => void = function (mode: ThemeMode): void {
  if (
    typeof window !== 'undefined' &&
    typeof window.afSetThemeMode === 'function' &&
    window.afSetThemeMode !== afSetThemeMode
  ) {
    window.afSetThemeMode(mode);
  } else {
    applyThemeMode(mode);
  }
};

export let afToggleThemeMode: () => ThemeMode = function (): ThemeMode {
  if (
    typeof window !== 'undefined' &&
    typeof window.afToggleThemeMode === 'function' &&
    window.afToggleThemeMode !== afToggleThemeMode
  ) {
    return window.afToggleThemeMode();
  }
  return toggleThemeMode();
};

export let afCycleThemeMode: () => ThemeMode = function (): ThemeMode {
  if (
    typeof window !== 'undefined' &&
    typeof window.afCycleThemeMode === 'function' &&
    window.afCycleThemeMode !== afCycleThemeMode
  ) {
    return window.afCycleThemeMode();
  }
  return cycleThemeMode();
};

export let initThemeMode: (container?: HTMLElement | Document) => void = function (
  container?: HTMLElement | Document
): void {
  bindThemeToggleListeners(container);
};

export let initMobileMenu: () => void = function (): void {
  setupMobileMenu();
};

export function initThemeWidgets(): void {
  initAnnouncementBars();
  initNavFavicon();
  initScrollAnimations();
  initStickyNavScroll();
  initLivingElevationViewport();
  initAuthFormSync();
  initSocialShare();
  initThemeMode();
  initMobileMenu();

  hideEmptyValidationSummaries();
  cleanupEmptyFlashMessages();
  initAuthTabSwitchers();
  initPasswordToggles();
  initCarousels();
  initContactForm();
  initLightboxes();
  initNewsletterForm();
  initPreviewStudioListener();
  initShopifyStorefrontElementsInternal();
  registerDraftingStudioElement();

  if (typeof window !== 'undefined') {
    window.afHideEmptyValidationSummaries = hideEmptyValidationSummaries;
    window.afCleanupEmptyFlashMessages = cleanupEmptyFlashMessages;
    window.afOpenCartDrawer = openCartDrawer;
    window.afCloseCartDrawer = closeCartDrawer;
    window.afToggleCartDrawer = toggleCartDrawer;
    window.afAddShopifyItem = addShopifyItem;
    window.afGetShopifyCart = () => getShopifyCartState();
    window.afClearShopifyCart = clearShopifyCart;
    window.afUpdateCartBadge = updateCartBadge;
    window.afSelectProductVariant = selectProductVariantInternal;
    window.afInitProductVariantSelectors = initProductVariantSelectorsInternal;
    window.afInitShopifyAppBlocks = initShopifyAppBlocksInternal;
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', initThemeWidgets);
  if (document.readyState !== 'loading') {
    initThemeWidgets();
  }
}
